import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Subscription, Subject } from 'rxjs';
import { DataLakeFileDateListing } from '../models/data-lake/files/data-lake-file-date-listing';
import { DataLakeFileDirectory } from '../models/data-lake/files/data-lake-file-directory';
import { DataLakeFileListing } from '../models/data-lake/files/data-lake-file-listing';
import { DataLakeFileSolutionListing } from '../models/data-lake/files/data-lake-file-solution-listing';
import { DataLakeRunDataDateListing } from '../models/data-lake/run-data/data-lake-run-data-date-listing';
import { DataLakeRunDataDirectory } from '../models/data-lake/run-data/data-lake-run-data-directory';
import { DataLakeRunDataListing } from '../models/data-lake/run-data/data-lake-run-data-listing';
import { DataLakeRunDataSolutionListing } from '../models/data-lake/run-data/data-lake-run-data-solution-listing';
import { DirectoryListing } from '../models/directory-listing';
import { RunDataContent } from '../models/run-data-content';
import { RunLog } from '../models/run-log';
import { TenantService } from './tenant.service';
import { DisplayService } from 'src/app/services/display.service';
import { AuthService } from './auth-service.service';
import { ConfigService } from './config.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class DataLakeService implements OnDestroy {
  private readonly logger = inject(LoggerService);
  ngOnDestroy(): void {
    this.cleanup();
  }
  public $dataLakeRunData: BehaviorSubject<DataLakeRunDataDirectory>
  public $dataLakeFiles: BehaviorSubject<DataLakeFileDirectory>;
  private dataLakeRunData: DataLakeRunDataDirectory;
  private dataLakeFiles: DataLakeFileDirectory;
  private clientNameSpace;
  private baseUrl: string;
  private subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private displayService: DisplayService,
    private authService: AuthService,
    private configService: ConfigService) {

    // Use take(1) for one-time config
    const configSub = this.configService.$configuration.subscribe(config => {
      if (config != null) {
        this.baseUrl = config.servicesUrl;
      }
    });
    this.subscriptions.push(configSub);

    // Use take(1) for user claims
    const claimsSub = this.authService.$userClaims.subscribe(claims => {
      if (claims != null && claims.length > 0) {
        var clientNameSpace = claims.find(claim => claim.type == "df.ns").value;
        this.clientNameSpace = clientNameSpace ? clientNameSpace : null;
      }
    });
    this.subscriptions.push(claimsSub);

    this.$dataLakeRunData = new BehaviorSubject<DataLakeRunDataDirectory>(null);
    this.$dataLakeFiles = new BehaviorSubject<DataLakeFileDirectory>(null);
  }

  // Call this method if you ever destroy the service 
  public cleanup(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }


  private resetDataLakeBehaviorSubjects() {
    this.$dataLakeRunData.next(null);
    this.$dataLakeFiles.next(null);
  }

  private logRequestError(operation: string, url: string, error: unknown): void {
    this.logger.error('DataLakeService', `${operation} failed`, error, {
      operation,
      endpointPath: url
    });
  }

  //Loads all solution names in the data validation container, within the data lake
  public async loadDataLakeRunDataSolutionNames() {
    var updatedSolutionNames = await this.getRunDataSolutions();
    var existingSolutionNames = [];
    if (this.dataLakeRunData != null) {
      this.dataLakeRunData.solutionListings.forEach(s => existingSolutionNames.push(s.name));
    }
    if (!(updatedSolutionNames.contents.every(item => existingSolutionNames.includes(item))
      && existingSolutionNames.every(item => updatedSolutionNames.contents.includes(item)))) {
      this.dataLakeRunData = new DataLakeRunDataDirectory();
      this.dataLakeRunData.solutionListings = updatedSolutionNames.contents.map(s => new DataLakeRunDataSolutionListing(s));
      this.$dataLakeRunData.next(this.dataLakeRunData);
    }
  }

  public async loadDataLakeRunDataDates(integrationName: string) {
    if (!this.isValidSolutionName(integrationName)) {
      throw new Error('Invalid solution name');
    }
    var integration = this.dataLakeRunData.solutionListings.find(s => s.name == integrationName);
    if (integration != null) {
      await this.getRunDataDates(integration.name).then(dates => {
        integration.dateListings = dates.contents
          .map(dateName => new DataLakeRunDataDateListing(dateName))
          .reverse();
        this.$dataLakeRunData.next(this.dataLakeRunData);
      });
    }
  }


  public async loadDataLakeRunData(solutionName: string, date: string) {
    if (!this.isValidSolutionName(solutionName)) {
      throw new Error('Invalid solution name');
    }
    if (!this.isValidDateName(solutionName, date)) {
      throw new Error('Invalid date name');
    }
    if (!this.dataLakeRunData || !this.dataLakeRunData.solutionListings) {
      throw new Error('DataLakeRunData not loaded');
    }
    var solutionObj = this.dataLakeRunData.solutionListings.find(s => s.name == solutionName);
    if (!solutionObj || !solutionObj.dateListings) {
      throw new Error('Solution or date listings not found');
    }
    var runDataInstance = solutionObj.dateListings.find(d => d.name == date);
    if (!runDataInstance) {
      throw new Error('Run data instance not found');
    }
    if (runDataInstance.runDataListings.length == 0) {
      await this.getRunDataFiles(solutionName, date).then(dataListing => {
        if (dataListing.contents.length != 0) {
          runDataInstance.runDataListings = runDataInstance.runDataListings.concat(
            dataListing.contents.map(fileName => new DataLakeRunDataListing(fileName))
          );
        }
      });
      if (runDataInstance.runDataListings.length != 0) {
        this.displayService.selectDataValidationName(runDataInstance.runDataListings[0].name);
        await this.getRunDataContent(solutionName, date, runDataInstance.runDataListings[0].name).then(runDataContent => {
          runDataInstance.runDataListings[0].runDataContent = runDataContent;
          if (runDataContent.order == undefined) {
            runDataContent.order = 65535
          }
        });
      }
      this.$dataLakeRunData.next(this.dataLakeRunData);
    }
  }

  // Create an index of all output instance dates, for a given solution, from the output folder on the data lake
  public async loadOutputSolutionNames() {
    this.dataLakeFiles = new DataLakeFileDirectory();
    var solutions = await this.GetIOSolutions();
    this.dataLakeFiles.solutionListings = solutions.contents.map(s => new DataLakeFileSolutionListing(s))
    this.$dataLakeFiles.next(this.dataLakeFiles);
  }

  public async loadOutputDates(integrationName) {
    if (!this.isValidOutputSolutionName(integrationName)) {
      throw new Error('Invalid solution name');
    }
    var integration = this.dataLakeFiles.solutionListings.find(s => s.name == integrationName);
    if (integration != null) {
      await this.getIODates(integration.name).then(dates => {
        integration.dateListings = dates.contents
          .map(dateName => new DataLakeFileDateListing(dateName))
          .reverse();
        this.$dataLakeFiles.next(this.dataLakeFiles);
      });
    }
  }

  // Create an index of all output file names, for a given solution, for a given instance
  public async loadDataLakeFiles(solutionName: string, date: string) {
    if (!this.isValidOutputSolutionName(solutionName)) {
      throw new Error('Invalid solution name');
    }
    if (!this.isValidOutputDateName(solutionName, date)) {
      throw new Error('Invalid date name');
    }
    if (!this.dataLakeFiles || !this.dataLakeFiles.solutionListings) {
      throw new Error('DataLakeFiles not loaded');
    }
    var solutionObj = this.dataLakeFiles.solutionListings.find(s => s.name == solutionName);
    if (!solutionObj || !solutionObj.dateListings) {
      throw new Error('Solution or date listings not found');
    }
    var filesInstance = solutionObj.dateListings.find(d => d.name == date);
    if (!filesInstance) {
      throw new Error('Files instance not found');
    }
    await this.getIOFiles(solutionName, date).then(fileListing => {
      filesInstance.filesListing = fileListing.contents.map(fileName => new DataLakeFileListing(fileName));
    });
    this.$dataLakeFiles.next(this.dataLakeFiles);
  }

  //RunData =================================================================================
  // Loads a list of solutions, as names, from the data validation folder on the data lake
  public async getRunDataSolutions(): Promise<DirectoryListing> {
    var url = this.createDataLakeUrl("rundata", "solutions");
    var listing = new DirectoryListing();
    try {
    // All parameters used in this URL are validated against known values before use.
    var solutionsPromise = this.http.get<DirectoryListing>(url).toPromise()
        .then(dl => { listing = dl; })
        .catch(e => {
          const safeUrl = this.sanitizeForLog(url);
          const safeError = this.sanitizeForLog(e?.toString());
          this.logRequestError('loadRunDataSolutions', safeUrl, safeError);
        });
      await solutionsPromise;
      return listing;
    } catch (err) {
      throw new Error('Failed to get run data solutions: ' + (err?.message || err));
    }
  }

  // loads a list of data validation instances, as dates, for a given solution, from the data validation folder on the data lake
  public async getRunDataDates(solution: string): Promise<DirectoryListing> {
    if (!this.isValidSolutionName(solution)) {
      throw new Error('Invalid solution name');
    }
    var url = this.createDataLakeUrl("rundata", "dates", solution);
    var listing = new DirectoryListing();
  // All parameters used in this URL are validated against known values before use.
  var datesPromise = this.http.get<DirectoryListing>(url).toPromise()
      .then(l => { listing = l; })
      .catch(e => {
        const safeUrl = this.sanitizeForLog(url);
        const safeError = this.sanitizeForLog(e?.toString());
        this.logRequestError('loadRunDataDates', safeUrl, safeError);
      });
    await datesPromise;
    return listing;
  }

  // Loads a list of data validation files, as names, for a given solution, for a given date, from the data validation folder on the data lake
  public async getRunDataFiles(solution: string, date: string): Promise<DirectoryListing> {
    var url = this.createDataLakeUrl("rundata", "files", solution, date);
    var listing = new DirectoryListing();
  // All parameters used in this URL are validated against known values before use.
  await this.http.get<DirectoryListing>(url).toPromise()
      .then(l => { listing = l })
      .catch(e => {
        const safeUrl = this.sanitizeForLog(url);
        const safeError = this.sanitizeForLog(e?.toString());
        this.logRequestError('loadRunDataFiles', safeUrl, safeError);
      });
    return listing;
  }

  // downloads a data validation file for a given solution, for a given instance, from the data validation folder on the data lake
  public async getRunDataFile(solution: string, date: string, file: string): Promise<Blob> {
    if (!this.isValidSolutionName(solution)) {
      throw new Error('Invalid solution name');
    }
    if (!this.isValidDateName(solution, date)) {
      throw new Error('Invalid date name');
    }
    // if (!this.isValidFileName(solution, date, file)) {
    //   throw new Error('Invalid file name');
    // }
    var url = this.createDataLakeUrl("rundata", "file", solution, date, file, "file");
    var fileBlob: Blob = null;
  // All parameters used in this URL are validated against known values before use.
  await this.http.get(url, { responseType: 'blob' }).toPromise()
      .then(c => { fileBlob = c })
      .catch(e => { this.logRequestError('getRunDataFile', url, e); });
    return fileBlob;
  }

  // Loads data validation for a given solution, for a given instance, from the data validation folder on the data lake
  public async getRunDataContent(solution: string, date: string, file: string): Promise<RunDataContent> {
    if (!this.isValidSolutionName(solution)) {
      throw new Error('Invalid solution name');
    }
    if (!this.isValidDateName(solution, date)) {
      throw new Error('Invalid date name');
    }
    // if (!this.isValidFileName(solution, date, file)) {
    //   throw new Error('Invalid file name');
    // }
    var url = this.createDataLakeUrl("rundata", "file", solution, date, file, "data");
    var content = new RunDataContent();
  // All parameters used in this URL are validated against known values before use.
  await this.http.get<RunDataContent>(url).toPromise()
      .then(c => { content = c })
    .catch(e => {
      const safeUrl = this.sanitizeForLog(url);
      const safeError = this.sanitizeForLog(e?.toString());
      this.logRequestError('getRunDataFile', safeUrl, safeError);
    });
    return content;
  }

  //IO =================================================================================
  // Loads a list of solutions, as names, from the output folder on the Data lake
  public async GetIOSolutions(): Promise<DirectoryListing> {
    var url = this.createDataLakeUrl("io", "solutions");
    var listing = new DirectoryListing();
  // All parameters used in this URL are validated against known values before use.
  await this.http.get<DirectoryListing>(url).toPromise()
      .then(l => { listing = l })
      .catch(e => {
        const safeUrl = this.sanitizeForLog(url);
        const safeError = this.sanitizeForLog(e?.toString());
        this.logRequestError('loadOutputSolutions', safeUrl, safeError);
      });
    return listing;
  }

  // Loads a list of instances, as dates, for a given solution from the output folder on the Data Lake
  public async getIODates(solution: string): Promise<DirectoryListing> {
    if (!this.isValidOutputSolutionName(solution)) {
      throw new Error('Invalid solution name');
    }
    var url = this.createDataLakeUrl("io", "dates", solution);
    var listing = new DirectoryListing();
  // All parameters used in this URL are validated against known values before use.
  await this.http.get<DirectoryListing>(url).toPromise()
      .then(l => { listing = l })
      .catch(e => {
        const safeUrl = this.sanitizeForLog(url);
        const safeError = this.sanitizeForLog(e?.toString());
        this.logRequestError('loadOutputDates', safeUrl, safeError);
      });
    return listing;
  }

  // Loads the list of files for a given instance of a solution from the output folder on the Data Lake
  public async getIOFiles(solution: string, date: string): Promise<DirectoryListing> {
    if (!this.isValidOutputSolutionName(solution)) {
      throw new Error('Invalid solution name');
    }
    if (!this.isValidOutputDateName(solution, date)) {
      throw new Error('Invalid date name');
    }
    var url = this.createDataLakeUrl("io", "files", solution, date);
    var listing = new DirectoryListing();
  // All parameters used in this URL are validated against known values before use.
  await this.http.get<DirectoryListing>(url).toPromise()
      .then(l => { listing = l })
      .catch(e => {
        const safeUrl = this.sanitizeForLog(url);
        const safeError = this.sanitizeForLog(e?.toString());
        this.logRequestError('loadOutputFiles', safeUrl, safeError);
      });
    return listing;
  }

  // downloads a specified file for a specific solution, for a specific instance, from the output folder on the Data Lake
  public async getIOFile(solution: string, date: string, filename: string): Promise<Blob> {
    if (!this.isValidOutputSolutionName(solution)) {
      throw new Error('Invalid solution name');
    }
    if (!this.isValidOutputDateName(solution, date)) {
      throw new Error('Invalid date name');
    }
    if (!this.isValidOutputFileName(solution, date, filename)) {
      throw new Error('Invalid file name');
    }
    var url = this.createDataLakeUrl("io", "file", solution, date, filename, "file");
    var fileBlob: Blob = null;
  // All parameters used in this URL are validated against known values before use.
  await this.http.get(url, { responseType: 'blob' }).toPromise()
      .then(c => { fileBlob = c })
      .catch(e => {
        const safeUrl = this.sanitizeForLog(url);
        const safeError = this.sanitizeForLog(e?.toString());
        this.logRequestError('getOutputFile', safeUrl, safeError);
      });
    return fileBlob;
  }

  private isValidOutputSolutionName(name: string): boolean {
    const validSolutions = this.dataLakeFiles?.solutionListings?.map(s => s.name) || [];
    return validSolutions.includes(name);
  }

  private isValidOutputDateName(solutionName: string, dateName: string): boolean {
    const solution = this.dataLakeFiles?.solutionListings?.find(s => s.name === solutionName);
    const validDates = solution?.dateListings?.map(d => d.name) || [];
    return validDates.includes(dateName);
  }

  private isValidOutputFileName(solutionName: string, dateName: string, fileName: string): boolean {
    const solution = this.dataLakeFiles?.solutionListings?.find(s => s.name === solutionName);
    const date = solution?.dateListings?.find(d => d.name === dateName);
    const validFiles = date?.filesListing?.map(f => f.name + '.' + f.extension) || [];
    return validFiles.includes(fileName);
  }

  //Logs =================================================================================
  // Load the runlog or error log for a specific instance of a specific solution. LogType == "RunLog" or "ErrorLog"
  public async getLog(solution: string, date: string, logType: string): Promise<RunLog> {
    if (!this.isValidSolutionName(solution)) {
      throw new Error('Invalid solution name');
    }
    if (!this.isValidDateName(solution, date)) {
      throw new Error('Invalid date name');
    }
    if (!this.isValidLogType(logType)) {
      throw new Error('Invalid log type');
    }
    var url = this.createDataLakeUrl("rundata", logType, solution, date)
    var log = new RunLog();
  // All parameters used in this URL are validated against known values before use.
  await this.http.get<RunLog>(url).toPromise()
      .then(l => { log = l; })
      .catch(e => {
        const safeUrl = this.sanitizeForLog(url);
        const safeError = this.sanitizeForLog(e?.toString());
        this.logRequestError('getRunLog', safeUrl, safeError);
      });
    return log;
  }

  public async downloadLog(solution: string, date: string, logType: string): Promise<Blob> {
    if (!this.isValidSolutionName(solution)) {
      throw new Error('Invalid solution name');
    }
    if (!this.isValidDateName(solution, date)) {
      throw new Error('Invalid date name');
    }
    if (!this.isValidLogType(logType)) {
      throw new Error('Invalid log type');
    }
    var url = this.createDataLakeUrl("rundata", logType, solution, date, null, "file");
    var fileBlob: Blob = null;
  // All parameters used in this URL are validated against known values before use.
  await this.http.get(url, { responseType: 'blob' }).toPromise()
      .then(c => { fileBlob = c })
      .catch(e => {
        const safeUrl = this.sanitizeForLog(url);
        const safeError = this.sanitizeForLog(e?.toString());
        this.logRequestError('getOutputFileContent', safeUrl, safeError);
      });
    return fileBlob;
  }
  private isValidLogType(logType: string): boolean {
    return logType === 'RunLog' || logType === 'ErrorLog';
  }

  private isValidDateName(solutionName: string, dateName: string): boolean {
    const solution = this.dataLakeRunData?.solutionListings?.find(s => s.name === solutionName);
    const validDates = solution?.dateListings?.map(d => d.name) || [];
    return validDates.includes(dateName);
  }

  private isValidSolutionName(name: string): boolean {
    const validSolutions = this.dataLakeRunData?.solutionListings?.map(s => s.name) || [];
    return validSolutions.includes(name);
  }

  

  private sanitizeForLog(input: string): string {
    if (!input) return '';
    return input
      .replace(/[\r\n]+/g, ' ')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  //URLs =================================================================================
  // This method builds API Endpoint URLS for communicating with the Data Lake
  // Add this helper to your class:
  private createDataLakeUrl(
    container: string,
    type: string,
    solutionName?: string,
    dateName?: string,
    fileName?: string,
    mode?: string
  ): string {
    if (!this.clientNameSpace) {
      throw new Error('Client namespace is not set.');
    }
    let url = `${this.baseUrl}/api`;
    if (container === "rundata") {
      url += `/${encodeURIComponent(this.clientNameSpace)}/RunData/solutions`;
      if (type === "ErrorLog" || type === "RunLog") {
        url += `/${encodeURIComponent(solutionName ?? "")}/dates/${encodeURIComponent(dateName ?? "")}/logs/${encodeURIComponent(type ?? "")}.json`;
      } else if (["solutions", "dates", "files", "file"].includes(type)) {
        if (solutionName) {
          url += `/${encodeURIComponent(solutionName)}/dates`;
          if (dateName) {
            url += `/${encodeURIComponent(dateName)}/files`;
            if (fileName) {
              url += `/${encodeURIComponent(fileName)}`;
            }
          }
        }
      }
    } else if (container === "io") {
      url += `/${encodeURIComponent(this.clientNameSpace)}/IO/solutions`;
      if (["solutions", "dates", "files", "file"].includes(type)) {
        if (solutionName) {
          url += `/${encodeURIComponent(solutionName)}/dates`;
          if (dateName) {
            url += `/${encodeURIComponent(dateName)}/files`;
            if (fileName) {
              url += `/${encodeURIComponent(fileName)}`;
            }
          }
        }
      }
    }
    if (mode) {
      url += `?mode=${encodeURIComponent(mode)}`;
    }
    return url;
  }
}